import {
  ButtonERP,
  DataTablePrimeReact,
  DropdownPrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { ContactoReadingDtoProps } from "api/DTOs/ContactoReadingDto"
import { PresupuestoReadingFiltersDtoProps } from "api/DTOs/PresupuestoReadingFiltersDto"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import {
  ByFilterPresupuestoQueryParamsProps,
  getPresupuestoByFilter,
  getPresupuestoByIdSimple,
} from "api/Presupuesto.service"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import axios from "axios"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import useCrearDocumento from "common/hooks/useEnviarDocumento"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import {
  OPTIONS_DOC,
  OPTIONS_PRESUPUESTOS_CONTACTO,
} from "features/Contactos/constants"
import { useConvertirDocumentoRedirect } from "features/Documentos/hooks/useConvertirDocumentoRedirect.hook"
import { handleDownloadPdfPresupuesto } from "features/Documentos/pages/Presupuestos/constants/constants"
import { PRESUPUESTOS_ENVIADOS_COLUMNS_ERP } from "features/Documentos/pages/Presupuestos/constants/PresupuestosEnviadosColumns"
import { PRESUPUESTOS_RECIBIDOS_COLUMNS_ERP } from "features/Documentos/pages/Presupuestos/constants/PresupuestosRecibidosColumns"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import * as ROUTES from "routes/RoutePaths"
import FilterMenu from "./FilterMenu"

const PresupuestosContatos = ({
  contacto,
}: {
  contacto: ContactoReadingDtoProps
}) => {
  const { t } = useTranslation()
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const dtRef = useRef<DataTablePrimeReact<
    PresupuestoReadingFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const { activeCompany: empresaActiva } = useUsuarioEmpresaActiva()
  const { crearDocumentoConLibrado, crearDocumentoConLibrador } =
    useCrearDocumento()
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: PresupuestoReadingFiltersDtoProps | null
  }>({
    show: false,
    document: null,
  })
  const [tableSelected, setTableSelected] = useState(OPTIONS_DOC.VENTA)
  const { handleConvertirDocumento } = useConvertirDocumentoRedirect()

  const ventasCancelToken = useRef(axios.CancelToken.source())
  const comprasCancelToken = useRef(axios.CancelToken.source())
  const fetchItemsVentas = useCallback(() => {
    return (queryState: ByFilterPresupuestoQueryParamsProps) => {
      if (!contacto.nif) return
      ventasCancelToken.current?.cancel()
      ventasCancelToken.current = axios.CancelToken.source()
      return getPresupuestoByFilter(
        {
          ...queryState,
          MaxResultCount: queryState.MaxResultCount ?? 100,
          SortingCriteria:
            queryState.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
          ClienteNif: contacto.nif,
          LibradorId: empresaActiva.id,
        },
        ventasCancelToken.current.token
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    items: itemsVentas,
    isLoading: isLoadingVentas,
    totalCount: totalCountVentas,
    setServiceQuery: setServiceQueryVentas,
  } = useFilterMenu({
    serviceCall: fetchItemsVentas,
  })

  const fetchItemsCompras = useCallback(() => {
    return (queryState: ByFilterPresupuestoQueryParamsProps) => {
      if (!contacto.nif) return
      comprasCancelToken.current?.cancel()
      comprasCancelToken.current = axios.CancelToken.source()
      return getPresupuestoByFilter(
        {
          ...queryState,
          MaxResultCount: queryState.MaxResultCount ?? 100,
          SortingCriteria:
            queryState.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
          ProveedorNif: contacto.nif,
          LibradoId: empresaActiva.id,
        },
        comprasCancelToken.current.token
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    items: itemsCompras,
    isLoading: isLoadingCompras,
    totalCount: totalCountCompras,
    setServiceQuery: setServiceQueryCompras,
  } = useFilterMenu({
    serviceCall: fetchItemsCompras,
  })

  const createPresupuesto = () => {
    tableSelected === OPTIONS_DOC.VENTA
      ? crearDocumentoConLibrado(
          ROUTES.CREAR_PRESUPUESTO,
          contacto,
          empresaActiva
        )
      : crearDocumentoConLibrador(
          ROUTES.CREAR_PRESUPUESTO,
          empresaActiva,
          contacto
        )
  }

  const handleOpenModalDocumento = async (id: string) => {
    const presupuestoById = await getPresupuestoByIdSimple(id)
    setModalDocumentoDetail({
      show: true,
      document: presupuestoById,
    })
  }

  return (
    <section className='tw-flex tw-flex-col tw-h-full'>
      <div className='tw-flex tw-flex-row tw-w-full tw-justify-between tw-px-6 tw-py-3'>
        <DropdownPrimeReact
          options={OPTIONS_PRESUPUESTOS_CONTACTO(t, {
            ventas: totalCountVentas,
            compras: totalCountCompras,
          })}
          optionLabel='name'
          optionValue='value'
          onChange={(e) => {
            setTableSelected(e.value)
          }}
          value={tableSelected}
          pt={{
            root: {
              className: "tw-bg-transparent tw-border-none",
            },
            input: {
              className: "tw-text-2xl",
            },
          }}
        />
        <span className='tw-flex tw-flex-row tw-gap-3 tw-items-center'>
          <FilterMenu
            serviceQuery={(
              query: Partial<ByFilterPresupuestoQueryParamsProps>
            ) => {
              setServiceQueryVentas(query)
              setServiceQueryCompras(query)
            }}
            sortingCriteria={sortingCriteria}
            maxResultCount={pagination.itemsPerPage}
            skipCount={pagination.skipCount}
            resetPagination={handleResetPagination}
          />

          <ButtonERP
            customize={{
              size: "md",
              type: "secondary",
            }}
            label={`${t("crear.presupuesto.addOrder")}`}
            onClick={createPresupuesto}
          />
        </span>
      </div>
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-72px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)]'>
          <TableERP
            refTable={dtRef}
            columns={
              tableSelected === OPTIONS_DOC.VENTA
                ? PRESUPUESTOS_ENVIADOS_COLUMNS_ERP({
                    t,
                    handleDownloadPdfPresupuesto,
                    handleConvertirDocumento,
                    showContacto: false,
                  })
                : PRESUPUESTOS_RECIBIDOS_COLUMNS_ERP({
                    t,
                    handleDownloadPdfPresupuesto,
                    handleConvertirDocumento,
                    showContacto: false,
                  })
            }
            className='tw-h-full !tw-z-0'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={
              tableSelected === OPTIONS_DOC.VENTA ? itemsVentas : itemsCompras
            }
            onRowClick={({ data }) => {
              if (!data.id) return
              handleOpenModalDocumento(data.id)
            }}
            showGridlines
            lazy
            dataKey='id'
            scrollable
            selectionMode='checkbox'
            size='small'
            selection={[]}
            loading={
              tableSelected === OPTIONS_DOC.VENTA
                ? isLoadingVentas
                : isLoadingCompras
            }
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/presupuestos-de-${
                  tableSelected === OPTIONS_DOC.COMPRA ? "compra" : "venta"
                }.svg`}
                title={
                  tableSelected === OPTIONS_DOC.COMPRA
                    ? "crear.presupuesto.buyLabel"
                    : "crear.presupuesto.saleLabel"
                }
                description='shared.noDataDescription'
                actionLabel='crear.presupuesto.addOrder'
                actionCommand={createPresupuesto}
              />
            }
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={
            tableSelected === OPTIONS_DOC.VENTA
              ? totalCountVentas
              : totalCountCompras
          }
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => {
            dtRef.current && dtRef.current?.resetScroll()
          }}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>
      <ModalVerDocumento
        showModal={modalDocumentoDetail.show}
        onHide={() =>
          setModalDocumentoDetail({
            show: false,
            document: null,
          })
        }
        docType={TIPO_DOCUMENTO_ENUM.PRESUPUESTO}
        documentoID={modalDocumentoDetail.document?.id ?? null}
        typeDocumentTable='presupuestos'
        isVenta={tableSelected === OPTIONS_DOC.VENTA}
      />
    </section>
  )
}

export default PresupuestosContatos
